import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ClassActionStatus, getClassActionStatusTranslationKey } from '@tvia-ai/shared';
import ComingSoonBanner from '../components/ui/ComingSoonBanner';

interface ClassAction {
  id: string;
  title: string;
  description: string;
  company: string;
  category: string;
  status: ClassActionStatus;
  deadline: string;
  participants: number;
  potentialReward: string;
  image?: string;
}

const ClassActionsPage: React.FC = () => {
  const { t } = useTranslation();
  const [classActions, setClassActions] = useState<ClassAction[]>([]);
  const [filteredActions, setFilteredActions] = useState<ClassAction[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [isLoading, setIsLoading] = useState(true);

  // Mock data - in a real app, this would come from an API
  const mockClassActions: ClassAction[] = [
    {
      id: '1',
      title: 'Data Privacy Violation - TechCorp',
      description: 'Class action lawsuit against TechCorp for unauthorized collection and sale of user data without consent.',
      company: 'TechCorp Inc.',
      category: 'Privacy',
      status: ClassActionStatus.Active,
      deadline: '2024-12-31',
      participants: 15420,
      potentialReward: '$500 - $2,000',
      image: '/images/techcorp-logo.png'
    },
    {
      id: '2',
      title: 'Wage Theft - RetailChain',
      description: 'Employees allege systematic wage theft through unpaid overtime and meal break violations.',
      company: 'RetailChain Corp.',
      category: 'Employment',
      status: ClassActionStatus.Active,
      deadline: '2024-11-15',
      participants: 8230,
      potentialReward: '$1,000 - $5,000',
      image: '/images/retailchain-logo.png'
    },
    {
      id: '3',
      title: 'Product Liability - AutoMaker',
      description: 'Defective brake system causing safety concerns for vehicle owners.',
      company: 'AutoMaker Ltd.',
      category: 'Product Liability',
      status: ClassActionStatus.Pending,
      deadline: '2025-03-01',
      participants: 4560,
      potentialReward: '$2,000 - $10,000',
      image: '/images/automaker-logo.png'
    },
    {
      id: '4',
      title: 'Securities Fraud - FinanceCorp',
      description: 'Allegations of misleading financial statements and insider trading.',
      company: 'FinanceCorp International',
      category: 'Securities',
      status: ClassActionStatus.Active,
      deadline: '2024-10-30',
      participants: 12340,
      potentialReward: '$1,500 - $8,000',
      image: '/images/financecorp-logo.png'
    },
    {
      id: '5',
      title: 'Antitrust - PharmaGiant',
      description: 'Price fixing and market manipulation in pharmaceutical industry.',
      company: 'PharmaGiant Inc.',
      category: 'Antitrust',
      status: ClassActionStatus.Settled,
      deadline: '2024-08-15',
      participants: 28900,
      potentialReward: '$3,000 - $15,000',
      image: '/images/pharmagiant-logo.png'
    }
  ];

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setClassActions(mockClassActions);
      setFilteredActions(mockClassActions);
      setIsLoading(false);
    }, 1000);
  }, []);

  useEffect(() => {
    let filtered = classActions;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(action =>
        action.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        action.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
        action.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(action => action.category === selectedCategory);
    }

    // Filter by status
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(action => action.status === selectedStatus);
    }

    setFilteredActions(filtered);
  }, [searchTerm, selectedCategory, selectedStatus, classActions]);

  const categories = ['all', 'Privacy', 'Employment', 'Product Liability', 'Securities', 'Antitrust'];
  const statuses = ['all', ClassActionStatus.Active, ClassActionStatus.Pending, ClassActionStatus.Settled];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'settled': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="bg-white rounded-lg shadow p-6">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-full mb-4"></div>
                  <div className="h-6 bg-gray-200 rounded w-1/3"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {t('classActions.title', 'Active Class Actions')}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('classActions.subtitle', 'Join thousands of people seeking justice through collective legal action. Find class action lawsuits that may affect you.')}
          </p>
        </div>

        {/* Coming Soon Banner */}
        <ComingSoonBanner />

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('classActions.search', 'Search')}
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('classActions.searchPlaceholder', 'Search lawsuits...')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>

            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('classActions.category', 'Category')}
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
              >
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category === 'all' ? t('classActions.allCategories', 'All Categories') : category}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('classActions.status', 'Status')}
              </label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
              >
                {statuses.map(status => (
                  <option key={status} value={status}>
                    {status === 'all' ? t('classActions.allStatuses', 'All Statuses') : t(getClassActionStatusTranslationKey(status as ClassActionStatus), status)}
                  </option>
                ))}
              </select>
            </div>

            {/* Results Count */}
            <div className="flex items-end">
              <p className="text-sm text-gray-600">
                {t('classActions.results', '{{count}} results', { count: filteredActions.length })}
              </p>
            </div>
          </div>
        </div>

        {/* Class Actions Grid */}
        {filteredActions.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">🔍</div>
            <h3 className="text-xl font-medium text-gray-900 mb-2">
              {t('classActions.noResults', 'No class actions found')}
            </h3>
            <p className="text-gray-600">
              {t('classActions.noResultsDescription', 'Try adjusting your search criteria or filters.')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredActions.map((action) => (
              <div key={action.id} className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                        {action.title}
                      </h3>
                      <p className="text-sm text-gray-600 mb-2">
                        {action.company}
                      </p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(action.status)}`}>
                      {t(getClassActionStatusTranslationKey(action.status), action.status)}
                    </span>
                  </div>

                  {/* Description */}
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {action.description}
                  </p>

                  {/* Details */}
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">{t('classActions.category', 'Category')}:</span>
                      <span className="text-gray-900">{action.category}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">{t('classActions.participants', 'Participants')}:</span>
                      <span className="text-gray-900">{action.participants.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">{t('classActions.potentialReward', 'Potential Reward')}:</span>
                      <span className="text-gray-900 font-medium">{action.potentialReward}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">{t('classActions.deadline', 'Deadline')}:</span>
                      <span className="text-gray-900">{formatDate(action.deadline)}</span>
                    </div>
                  </div>

                  {/* Action Button */}
                  <Link
                    to={`/join-lawsuit/${action.id}`}
                    className="w-full bg-cyan-600 text-white py-2 px-4 rounded-md hover:bg-cyan-700 transition-colors duration-200 text-center block"
                  >
                    {t('classActions.joinLawsuit', 'Join Lawsuit')}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* CTA Section */}
        <div className="mt-16 bg-gradient-to-r from-cyan-600 to-blue-600 rounded-lg p-8 text-center text-white">
          <h2 className="text-2xl font-bold mb-4">
            {t('classActions.ctaTitle', 'Can\'t find what you\'re looking for?')}
          </h2>
          <p className="text-lg mb-6 opacity-90">
            {t('classActions.ctaDescription', 'If you believe you have a case that could benefit from class action treatment, let us help you get started.')}
          </p>
          <Link
            to="/file-lawsuit"
            className="bg-white text-cyan-600 px-6 py-3 rounded-md font-medium hover:bg-gray-100 transition-colors duration-200"
          >
            {t('classActions.startNewLawsuit', 'Start New Lawsuit')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ClassActionsPage;